import { type IgvTrack } from '../features/viewers/igv/IgvMount'

export type TrackRole = 'proband' | 'comparator' | 'mother' | 'father' | 'sibling' | null

export type ApiTrack = {
  id: string
  label: string
  format: string
  kind: 'variant' | 'alignment' | 'annotation'
  url: string
  indexUrl: string | null
  role: TrackRole
}

/**
 * IGV display options reviewers actually reach for (GEN-3105). Defaults mirror
 * tgg-viewer's bam/vcf options: soft clips + strand shading on, VCFs expanded.
 */
export type IgvDisplaySettings = {
  viewAsPairs: boolean
  showSoftClips: boolean
  shadeByStrand: boolean
  /**
   * Alignment: color reads by insert size and pair orientation (igv.js
   * 'unexpectedPair', which combines pairOrientation + tlen). Surfaces
   * discordant pairs, the SOP's primary short-read evidence for CNVs
   * (enlarged-insert pairs at DEL/DUP breakpoints, inverted pairs for
   * inversions). Wins over strand shading when both are set, since a track
   * carries one colorBy (GEN-4446).
   */
  colorByInsertSize: boolean
  vcfExpanded: boolean
  /**
   * BAF: restrict the drawn track to the informative heterozygous band and hide
   * the homozygous rails at 0 / 1 (GEN-5301). The API sidecar now emits every
   * SNP site (GEN-5300, matching SeqOne's full scatter); this is a client-side
   * view filter, applied by tightening the wig y-axis to the het band so the
   * rails fall outside the drawn range. Off by default (full scatter).
   */
  bafHetOnly: boolean
}

export const DEFAULT_IGV_SETTINGS: IgvDisplaySettings = {
  viewAsPairs: false,
  showSoftClips: true,
  shadeByStrand: true,
  colorByInsertSize: false,
  vcfExpanded: true,
  bafHetOnly: false,
}

/**
 * Max bp span at which alignment (CRAM/BAM) tracks fetch and draw reads. igv.js
 * defaults to ~30 kb, so at a typical review locus (e.g. an ~80 kb CNV span)
 * reviewers see "Zoom in to see features". Raise it so reads load at a wider
 * view. This only changes when reads are fetched: per-base detail (mismatches,
 * soft clips, pairs) when zoomed in is unchanged, and whole-chromosome/gene-wide
 * views still fall back to coverage-only so the viewer stays responsive
 * (GEN-4349).
 */
export const ALIGNMENT_VISIBILITY_WINDOW_BP = 100_000

/**
 * Max bp span at which variant call tracks (SNV / CNV / SV / mito / eh) draw
 * their features. Unlike alignments, variant records are tiny points/segments,
 * so a wide window is cheap to render. igv.js's default hides them at
 * multi-gene zoom, leaving reviewers looking at a large event (e.g. a multi-gene
 * deletion) with "Zoom in to see features" on the very calls they want to see
 * (GEN-5376). 10 Mb covers realistic review spans (multi-gene / whole-arm CNVs)
 * while still falling back to nothing at whole-chromosome zoom (chromosomes are
 * 45-250 Mb), keeping the viewer responsive. Genome-wide bedGraph tracks (BAF /
 * coverage / depth-ratio) carry no window and are unaffected.
 */
export const VARIANT_VISIBILITY_WINDOW_BP = 10_000_000

/**
 * Fixed y-axis band for the CNV depth-ratio wig track (GEN-4447). The SOP depth
 * gate is defined against fixed multiples of the diploid baseline (CN=0 ~0x,
 * CN=1 ~0.5x, CN=2 ~1x, CN=3 ~1.5x, CN>=4 >=2x), so a fixed 0-2 scale keeps the
 * bands comparable across samples rather than autoscaling per view. The dotted
 * guide line marks the diploid baseline (ratio 1.0).
 */
export const DEPTH_TRACK_MIN = 0
export const DEPTH_TRACK_MAX = 2
export const DEPTH_TRACK_BASELINE = 1

/**
 * Genome-wide coverage-repartition track (GEN-4990 / GEN-5290). Derived from the
 * SNV VCF's binned FORMAT/DP by the API and served as a bedGraph annotation with
 * a `-coverage` track id. Coverage magnitude varies per sample, so the y-axis
 * autoscales rather than using a fixed cap (mirrors SeqOne's per-sample line).
 * Pinned above the depth track so it reads at the top at chromosome zoom.
 */
export const COVERAGE_TRACK_COLOR = 'rgb(0,140,60)'
export const COVERAGE_TRACK_ORDER = -10

/**
 * Genome-wide B-allele-frequency track (GEN-4990 / GEN-5292). Derived from the
 * SNV VCF's per-het-site alt fraction by the API and served as a bedGraph
 * annotation with a `-baf` track id. Fixed 0-1 y-axis with a guide at 0.5 (the
 * diploid het expectation); departures from the 0.5 band flag LOH / UPD / large
 * events. Pinned near the top alongside coverage.
 */
export const BAF_TRACK_MIN = 0
export const BAF_TRACK_MAX = 1
export const BAF_TRACK_BASELINE = 0.5
export const BAF_TRACK_COLOR = 'rgb(120,60,160)'
export const BAF_TRACK_ORDER = -9

/**
 * Het-band y-axis window for the "BAF het-only" view (GEN-5301). The API sidecar
 * emits every SNP site, so hom-ref clusters at ~0 and hom-alt at ~1. Tightening
 * the drawn axis to [0.1, 0.9] drops those rails from view and leaves the
 * informative heterozygous band centered on 0.5.
 */
export const BAF_HET_BAND_MIN = 0.1
export const BAF_HET_BAND_MAX = 0.9

export function toIgvTrack(track: ApiTrack, settings: IgvDisplaySettings): IgvTrack {
  const base: IgvTrack = {
    // Preserve the API track id so downstream features can identify a track by
    // its stable id suffix (e.g. -coverage / -baf / -depth) rather than the
    // human label, which varies (the depth track reads "... depth ratio").
    // igv.js keeps config.id as track.id (falling back to name otherwise).
    id: track.id,
    name: track.label,
    format: track.format,
    type: track.kind,
    url: track.url,
    ...(track.indexUrl ? { indexURL: track.indexUrl } : {}),
  }
  if (track.kind === 'annotation' && track.format === 'bedgraph') {
    // Three genome-wide bedGraph annotation tracks share this kind/format and
    // are disambiguated by their API track-id suffix. Each is a fixed- or
    // auto-scaled wig pinned above the alignments/variants so it reads at
    // chromosome-level zoom where per-variant tracks show "Zoom in".
    if (track.id.endsWith('-coverage')) {
      // Coverage repartition: mean FORMAT/DP per bin. Autoscaled y-axis since
      // coverage magnitude varies per sample (GEN-4990 / GEN-5290).
      return {
        ...base,
        type: 'wig',
        autoscale: true,
        height: 50,
        color: COVERAGE_TRACK_COLOR,
        order: COVERAGE_TRACK_ORDER,
      }
    }
    if (track.id.endsWith('-baf')) {
      // B-allele frequency: per-SNP alt fraction on a fixed 0-1 scale with a
      // guide at the diploid 0.5 expectation (GEN-4990 / GEN-5292). Rendered as
      // points, not the igv.js default bars: BAF is a per-site scatter, and
      // zero-anchored bars would fill 0..value and bury the 0.5 band the guide
      // is meant to highlight (LOH / UPD / large-event departures).
      //
      // The sidecar carries every SNP site (GEN-5300), so hom sites form rails
      // at 0/1. "Het-only" (GEN-5301) tightens the drawn y-axis to the het band
      // so those rails fall outside the range, leaving just the informative
      // scatter; off by default, showing the full SeqOne-style scatter.
      const [bafMin, bafMax] = settings.bafHetOnly
        ? [BAF_HET_BAND_MIN, BAF_HET_BAND_MAX]
        : [BAF_TRACK_MIN, BAF_TRACK_MAX]
      return {
        ...base,
        type: 'wig',
        graphType: 'points',
        autoscale: false,
        min: bafMin,
        max: bafMax,
        height: 50,
        color: BAF_TRACK_COLOR,
        guideLines: [{ y: BAF_TRACK_BASELINE, color: 'rgb(150,150,150)', dotted: true }],
        order: BAF_TRACK_ORDER,
      }
    }
    // The CNV depth-ratio sidecar (per-segment DPS[0]) renders as a wig track
    // on a fixed 0-2 scale with a baseline guide at 1.0 (GEN-4447). Placed just
    // above the alignments so it reads against the coverage pileup. This is the
    // default for any other (e.g. `-depth`) bedGraph annotation.
    return {
      ...base,
      type: 'wig',
      autoscale: false,
      min: DEPTH_TRACK_MIN,
      max: DEPTH_TRACK_MAX,
      height: 50,
      color: 'rgb(0,90,180)',
      guideLines: [{ y: DEPTH_TRACK_BASELINE, color: 'rgb(150,150,150)', dotted: true }],
      order: -8,
    }
  }
  if (track.kind === 'alignment') {
    // One colorBy per track: insert-size / pair-orientation takes precedence
    // over strand shading when both are enabled (GEN-4446). Fall back to an
    // explicit 'none' rather than omitting colorBy: igv.js defaults an omitted
    // colorBy to 'unexpectedPair', so omission would leave insert-size coloring
    // stuck on and make the toggle a no-op when strand shading is also off.
    const colorBy = settings.colorByInsertSize ? 'unexpectedPair' : settings.shadeByStrand ? 'strand' : 'none'
    return {
      ...base,
      visibilityWindow: ALIGNMENT_VISIBILITY_WINDOW_BP,
      viewAsPairs: settings.viewAsPairs,
      showSoftClips: settings.showSoftClips,
      colorBy,
    }
  }
  // Variant call tracks (SNV / CNV / SV / mito / eh). A wide visibility window
  // keeps the calls drawn across multi-gene spans so large events stay readable
  // when reads are not (GEN-5376); records are tiny so this is cheap.
  return {
    ...base,
    visibilityWindow: VARIANT_VISIBILITY_WINDOW_BP,
    displayMode: settings.vcfExpanded ? 'EXPANDED' : 'COLLAPSED',
  }
}
